import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "./auth/AuthProvider";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { TrialEndingBanner } from "./components/TrialEndingBanner";
import { SplashScreen } from "./components/SplashScreen";
import { WelcomeModal } from "./components/WelcomeModal";
import { CookieBanner } from "./components/CookieBanner";
import { DobReconfirmBanner } from "./components/DobReconfirmBanner";
import { SupportChatWidget } from "./components/SupportChatWidget";
import { GetAppRibbon } from "./components/GetAppRibbon";
import { ToyCycleLogo } from "./components/Logo";
import { RouteErrorBoundary } from "./components/RouteErrorBoundary";

// Eager: small pages on the critical homepage / auth path
import { MarketplacePage } from "./pages/Marketplace";
import { AuthPage } from "./pages/Auth";

// Lazy: heavy pages — code-split & loaded on demand
const DashboardPage           = lazy(() => import("./pages/Dashboard").then(m => ({ default: m.DashboardPage })));
const BrowsePage              = lazy(() => import("./pages/Browse").then(m => ({ default: m.BrowsePage })));
const ListingDetailPage       = lazy(() => import("./pages/ListingDetail").then(m => ({ default: m.ListingDetailPage })));
const CreateListingPage       = lazy(() => import("./pages/CreateListing").then(m => ({ default: m.CreateListingPage })));
const OrderSuccessPage        = lazy(() => import("./pages/OrderSuccess").then(m => ({ default: m.OrderSuccessPage })));
const LearningPage            = lazy(() => import("./pages/Learning").then(m => ({ default: m.LearningPage })));
const PricingPage             = lazy(() => import("./pages/Pricing").then(m => ({ default: m.PricingPage })));
const SubscriptionSuccessPage = lazy(() => import("./pages/SubscriptionSuccess").then(m => ({ default: m.SubscriptionSuccessPage })));
const MessagesPage            = lazy(() => import("./pages/Messages").then(m => ({ default: m.MessagesPage })));
const TrustSafetyPage         = lazy(() => import("./pages/TrustSafety"));
const FAQPage                 = lazy(() => import("./pages/FAQ"));
const TermsPage               = lazy(() => import("./pages/Terms"));
const PrivacyPage             = lazy(() => import("./pages/Privacy"));
const ParentalConsentPage     = lazy(() => import("./pages/ParentalConsent"));
const AdminModerationPage     = lazy(() => import("./pages/AdminModeration"));
const AdminExperimentsPage    = lazy(() => import("./pages/AdminExperiments"));
const AdminSupportPage        = lazy(() => import("./pages/AdminSupport"));
const AdminSupportMetricsPage = lazy(() => import("./pages/AdminSupportMetrics"));
const AdminBadResponsesPage   = lazy(() => import("./pages/AdminBadResponses"));
const AdminDisputesPage       = lazy(() => import("./pages/AdminDisputes"));
const ReferralsPage           = lazy(() => import("./pages/Referrals"));
const ContactPage             = lazy(() => import("./pages/Contact"));
const GetTheAppPage           = lazy(() => import("./pages/GetTheApp"));

const RouteFallback = () => (
  <div
    className="min-h-[60vh] flex items-center justify-center"
    data-testid="route-suspense-fallback"
    aria-busy="true"
    aria-live="polite"
  >
    <div className="flex flex-col items-center gap-3">
      <ToyCycleLogo size={56} />
      <span className="text-sm font-bold text-gray-600">Loading…</span>
    </div>
  </div>
);

function AppShell() {
  const location = useLocation();
  return (
    <div className="min-h-screen flex flex-col bg-[#FDFBF7]">
      <Toaster position="top-right" richColors />
      <GetAppRibbon />
      <Header />
      <TrialEndingBanner />
      <SplashScreen />
      <WelcomeModal />
      <DobReconfirmBanner />
      <CookieBanner />
      <SupportChatWidget />
      <main className="flex-1">
        <RouteErrorBoundary routeKey={location.pathname}>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<MarketplacePage />} />
              <Route path="/marketplace" element={<MarketplacePage />} />
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/browse" element={<BrowsePage />} />
              <Route path="/listing/:id" element={<ListingDetailPage />} />
              <Route path="/create-listing" element={<CreateListingPage />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/order-success" element={<OrderSuccessPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/legal/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/legal/privacy" element={<PrivacyPage />} />
              <Route path="/parental-consent/:token" element={<ParentalConsentPage />} />
              <Route path="/learning" element={<LearningPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/subscription-success" element={<SubscriptionSuccessPage />} />
              <Route path="/messages" element={<MessagesPage />} />
              <Route path="/trust" element={<TrustSafetyPage />} />
              <Route path="/admin/moderation" element={<AdminModerationPage />} />
              <Route path="/admin/experiments" element={<AdminExperimentsPage />} />
              <Route path="/admin/support" element={<AdminSupportPage />} />
              <Route path="/admin/support/metrics" element={<AdminSupportMetricsPage />} />
              <Route path="/admin/support/quality" element={<AdminBadResponsesPage />} />
              <Route path="/admin/disputes" element={<AdminDisputesPage />} />
              <Route path="/referrals" element={<ReferralsPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/support" element={<ContactPage />} />
              <Route path="/get-the-app" element={<GetTheAppPage />} />
              <Route path="/mobile-app" element={<GetTheAppPage />} />
            </Routes>
          </Suspense>
        </RouteErrorBoundary>
      </main>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppShell />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
